import {
  Button,
  Dropdown,
  DropdownContent,
  DropdownTrigger,
  RiArrowDownSLine,
  RiCompass3Line,
  Stack,
} from '@landler/component-library';
import bbox from '@turf/bbox';
import { BBox2d } from '@turf/helpers/dist/js/lib/geojson';
import { bboxPolygon, circle, difference, featureCollection } from '@turf/turf';
import { BBox, Feature, FeatureCollection, Point, Polygon } from 'geojson';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layer, MapRef, Source } from 'react-map-gl';
import { matchPath, useLocation } from 'react-router-dom';

import { FormRadioGroup, FormRadioItem } from '@/components/widgets/o/Form/FormRadio';
import { useControlsContext } from '@/components/widgets/o/Map/hooks/useFormControls';
import { useLandscapeView } from '@/hooks/useLandscapeView';
import { usePathSet } from '@/hooks/usePathSet';
import { isNullish } from '@/utils/booleans';

import { getRadiusCircleMask } from '../hooks/useBoundingBox';
import { NO_RADIUS, RadiusType, useMapRadiusContext } from './MapRadiusContextProvider';

const SHOW_RADIUS_LABEL = 'shared.projects.map.labels.showRadius';

export type ReportingBoundsLayerProps = {
  bounds: BBox2d;
  mapRef: MapRef | null;
  plotMarkers?: FeatureCollection<Point>;
  enableZoom?: boolean;
};

export const ReportingBoundsLayer = ({ bounds, mapRef, plotMarkers, enableZoom = true }: ReportingBoundsLayerProps) => {
  const pathSet = usePathSet();
  const { pathname } = useLocation();
  const controlsForm = useControlsContext();
  const [radius] = controlsForm.watch(['radius']);

  const isPlotPage = !!matchPath(pathSet.plot, pathname);
  const hasRadius = radius !== NO_RADIUS && !isNullish(radius);

  const [showRadius, setShowRadius] = useState(isPlotPage && hasRadius);

  return (
    <>
      <Stack direction='row' spacing={2} className='absolute right-6 top-6'>
        <RadiusComponent
          showRadius={showRadius}
          setShowRadius={setShowRadius}
          bounds={bounds}
          mapRef={mapRef}
          enableZoom={enableZoom}
        />
      </Stack>
      {showRadius && <SourceLayer plotMarkers={plotMarkers} />}
    </>
  );
};

const RadiusComponent = ({
  showRadius,
  setShowRadius,
  bounds,
  mapRef,
  enableZoom,
}: {
  showRadius: boolean;
  setShowRadius: (show: boolean) => void;
} & ReportingBoundsLayerProps) => {
  const controlsForm = useControlsContext();
  const [radius] = controlsForm.watch(['radius']);
  const radiusKm = isNullish(radius) || radius === NO_RADIUS ? null : Number(radius);

  const { circleGeoJSON } = getRadiusCircleMask({ bounds, radius: radiusKm });

  const pathSet = usePathSet();
  const { pathname } = useLocation();

  const PAGES_WITH_LANDSCAPE_VIEW = [pathSet.plot] as const;
  const hasPageLandscapeView = !!PAGES_WITH_LANDSCAPE_VIEW.find((allowedSite) => matchPath(allowedSite, pathname));

  const handleRadiusButtonClick = () => {
    const newShowRadius = !showRadius;
    setShowRadius(newShowRadius);

    if (newShowRadius) {
      if (!circleGeoJSON) return;

      const circleBbox = bbox(circleGeoJSON);
      if (enableZoom) updateMapBoundaries({ mapRef, bounds: circleBbox });
    } else if (enableZoom) updateMapBoundaries({ mapRef, bounds });
  };

  const handleRadiusRadioButtonChange = (newRadius: RadiusType) => {
    const { circleGeoJSON: newCircleGeoJSON } = getRadiusCircleMask({
      bounds,
      radius: newRadius === NO_RADIUS ? null : Number(newRadius),
    });

    setShowRadius(true);

    const circleBbox = bbox(newCircleGeoJSON);
    if (enableZoom) updateMapBoundaries({ mapRef, bounds: circleBbox });
  };

  const handleRadiusRadioButtonReset = () => {
    setShowRadius(false);
    if (enableZoom) updateMapBoundaries({ mapRef, bounds });

    if (radius !== NO_RADIUS) {
      controlsForm.setValue('radius', NO_RADIUS);
    }
  };

  if (!hasPageLandscapeView) {
    return <ShowRadiusButton onClick={handleRadiusButtonClick} />;
  }

  return (
    <ShowRadiusComponent
      onRadiusRadioButtonChange={handleRadiusRadioButtonChange}
      onRadiusRadioButtonReset={handleRadiusRadioButtonReset}
    />
  );
};

const SourceLayer = ({ plotMarkers }: { plotMarkers?: FeatureCollection<Point> }) => {
  const controlsForm = useControlsContext();
  const [radius] = controlsForm.watch(['radius']);

  if (isNullish(radius) || radius === NO_RADIUS) {
    return null;
  }

  const radiusKm = Number(radius);

  const circleFeatures =
    plotMarkers?.features.map((feature) => {
      const coords = feature.geometry.coordinates;
      return circle(coords, radiusKm, { steps: 64, units: 'kilometers' });
    }) ?? [];

  if (circleFeatures.length <= 0) return null;

  const world = bboxPolygon([-180, -90, 180, 90]);

  let allCirclesDifference = world;
  allCirclesDifference = circleFeatures.reduce((acc, curr) => {
    const diff = difference(acc, curr) as Feature<Polygon>;
    if (diff) return diff;
    return acc;
  }, allCirclesDifference);

  if (isNullish(allCirclesDifference)) return null;

  const mask = featureCollection([allCirclesDifference as Feature<Polygon>]);

  return (
    <Source id='mask-source' type='geojson' data={mask}>
      <Layer
        id='mask-layer'
        type='fill'
        paint={{
          'fill-color': '#000000',
          'fill-opacity': 0.7,
        }}
      />
    </Source>
  );
};

export const initializeMapZoom = ({
  bounds,
  mapRef,
  radius,
}: {
  radius: string;
} & ReportingBoundsLayerProps) => {
  const radiusKm = isNullish(radius) || radius === NO_RADIUS ? null : Number(radius);

  if (!radiusKm) return;

  const { circleGeoJSON } = getRadiusCircleMask({ bounds, radius: radiusKm });
  const circleBbox = bbox(circleGeoJSON);

  if (!circleBbox) return;

  updateMapBoundaries({ mapRef, bounds: circleBbox });
};

const updateMapBoundaries = ({ mapRef, bounds }: { bounds: BBox | BBox2d; mapRef: MapRef | null }) => {
  if (!mapRef) return;

  mapRef.fitBounds(
    [
      [bounds[0], bounds[1]],
      [bounds[2], bounds[3]],
    ],
    {
      padding: 50,
      duration: 1000,
    },
  );
};

const ShowRadiusComponent = ({
  onRadiusRadioButtonChange,
  onRadiusRadioButtonReset,
}: {
  onRadiusRadioButtonChange: (newRadius: RadiusType) => void;
  onRadiusRadioButtonReset: () => void;
}) => {
  const isLandsCapeViewActive = useLandscapeView().isActive;

  const onResetRef = useRef(onRadiusRadioButtonReset); // Prevent zoom reset on every component re-render

  useEffect(() => {
    if (!isLandsCapeViewActive) {
      onResetRef.current();
    }
  }, [isLandsCapeViewActive]);

  if (!isLandsCapeViewActive) {
    return null;
  }

  return <ShowRadiusDropdown onRadioButtonClick={onRadiusRadioButtonChange} onReset={onRadiusRadioButtonReset} />;
};

const ShowRadiusButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();

  const RADIUS = 30 as const;

  return (
    <Button
      onClick={onClick}
      variant='contained'
      color='white'
      size='small'
      className='text-text-primary'
      leftAdornment={<RiCompass3Line />}
      data-testid='show-radius-button'
    >
      {t(SHOW_RADIUS_LABEL, { radius: RADIUS })}
    </Button>
  );
};

const ShowRadiusDropdown = ({
  onRadioButtonClick,
  onReset,
}: {
  onRadioButtonClick: (newRadius: RadiusType) => void;
  onReset: () => void;
}) => {
  const { setRadius } = useMapRadiusContext();

  const { t } = useTranslation();

  const controlsForm = useControlsContext();
  const [radius] = controlsForm.watch(['radius']);

  const RADIUS_OPTIONS: RadiusType[] = ['5', '10', '15', '20', '25', '30'];

  const handleRadiusRadioButtonClick = (value: string | boolean | null | undefined) => {
    if (typeof value !== 'string') return;

    setRadius(value as RadiusType);

    if (value === NO_RADIUS) {
      onReset();
    } else {
      onRadioButtonClick(value as RadiusType);
    }
  };

  return (
    <Dropdown>
      <DropdownTrigger asChild>
        <Button
          variant='contained'
          color='white'
          size='small'
          data-testid='show-radius-dropdown-trigger'
          leftAdornment={<RiCompass3Line />}
          rightAdornment={<RiArrowDownSLine />}
        >
          {radius === NO_RADIUS ? t('shared.projects.map.labels.selectRadius') : t(SHOW_RADIUS_LABEL, { radius })}
        </Button>
      </DropdownTrigger>
      <DropdownContent className='mt-2 rounded-[20px]'>
        <FormRadioGroup
          name='radius'
          className='px-4 py-2'
          onValueChange={handleRadiusRadioButtonClick}
          controlsform={controlsForm}
        >
          <FormRadioItem label={t('shared.projects.map.labels.noRadius')} value={NO_RADIUS} />
          {RADIUS_OPTIONS.map((r) => (
            <FormRadioItem key={r} label={t(SHOW_RADIUS_LABEL, { radius: r })} value={r.toString()} />
          ))}
        </FormRadioGroup>
      </DropdownContent>
    </Dropdown>
  );
};
